import { AnalyticsUserEvent } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulLogoIcon,
    H5Regular,
    Icon,
    isWeb,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { BackgroundGradient } from '../../../common/components/BackgroundGradient';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { getRGBA } from '../../../common/utils/colorUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { track } from '../../../logger/Logger';
import { useUpdateMemberJourneyLog } from '../../../member-profile/hooks/useUpdateMemberJourneyLog';

const OnboardingSheet = () => {
    const { value: isGoyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const user = useAuthedUser();
    const { updateMemberJourneyLog } = useUpdateMemberJourneyLog();

    const [showOnboardingSheet, setShowOnboardingSheet] = useState(false);
    useEffect(() => {
        if (
            isGoyoEnabled &&
            !user.memberJourneyLog?.completedGatedOnboarding &&
            new Date(user.membership?.createdAt ?? '') >
                new Date('2026-10-30 09:00:00')
        ) {
            setShowOnboardingSheet(true);
        }
    }, [isGoyoEnabled, user.memberJourneyLog?.completedGatedOnboarding]);

    const onClose = () => {
        track(AnalyticsUserEvent.ONBOARDING_INFO_SHEET_CLOSED);
        setShowOnboardingSheet(false);
        void updateMemberJourneyLog({ completedGatedOnboarding: true });
    };

    const data = [
        { title: 'Activate your system ', subTitle: 'Bank accounts, cards' },
        {
            title: 'Fund your accounts',
            subTitle: 'Direct deposit, bills switching',
        },
        { title: 'Start spending safely', subTitle: 'Stress-free budgeting' },
        {
            title: 'Plan for the future',
            subTitle:
                'Investment accounts, bills & spend calibration, custom goals',
        },
    ];

    return (
        <Sheet
            isOpen={showOnboardingSheet}
            disableDrag={true}
            onClose={() => {
                setShowOnboardingSheet(false);
            }}
            showCloseButton={false}
            withPadding={false}
            dismissOnOverlayPress={false}
        >
            {isWeb ? (
                <View
                    position="absolute"
                    left={-72}
                    top={-123}
                    width={546}
                    height={246}
                    borderRadius={546}
                    pointerEvents="none"
                    style={{
                        background:
                            'radial-gradient(50% 50% at 50% 50%, rgba(193, 243, 221, 0.68) 0.01%, rgba(183, 210, 217, 0.27) 100%)',
                        filter: 'blur(50px)',
                    }}
                />
            ) : (
                <LinearGradient
                    colors={[
                        'rgba(193, 243, 221, 0.55)',
                        'rgba(188, 226, 219, 0.35)',
                        'rgba(183, 210, 217, 0.1)',
                        'rgba(183, 210, 217, 0)',
                    ]}
                    locations={[0, 0.3, 0.7, 1]}
                    start={{ x: 0.5, y: 0 }}
                    end={{ x: 0.5, y: 1 }}
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        height: 180,
                    }}
                    pointerEvents="none"
                />
            )}
            <YStack
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={24}
                paddingHorizontal={24}
                paddingTop={20}
                paddingBottom={24}
            >
                <Icon
                    icon={FruitfulLogoIcon}
                    width={40}
                    height={34}
                    color="$leaf"
                />
                <YStack
                    gap={8}
                    alignItems="center"
                    justifyContent="center"
                    width="90%"
                >
                    <H5Regular textAlign="center">
                        {user.firstName}, let&apos;s bring your finances to
                        Fruitful
                    </H5Regular>
                    <BodyMediumRegular textAlign="center" opacity={0.66}>
                        How to get started:
                    </BodyMediumRegular>
                </YStack>
                <View borderRadius={24} width="100%" overflow="hidden">
                    <BackgroundGradient />
                    <YStack paddingHorizontal={24} paddingTop={24}>
                        {data.map((item, i) => (
                            <XStack key={i} gap={16} alignItems="stretch">
                                <YStack width={32} alignItems="center">
                                    <View
                                        width={32}
                                        height={32}
                                        borderRadius={16}
                                        borderWidth={1}
                                        borderColor="$white"
                                        backgroundColor={getRGBA(
                                            'white',
                                            i === 0 ? 0.9 : 0.5,
                                        )}
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <BodyMediumRegular>
                                            {i + 1}
                                        </BodyMediumRegular>
                                    </View>
                                    <View
                                        width={2}
                                        flex={1}
                                        backgroundColor="$white"
                                    />
                                </YStack>
                                <YStack gap={4} flex={1} paddingBottom={24}>
                                    <BodyLargeEmphasized>
                                        {item.title}
                                    </BodyLargeEmphasized>
                                    <BodyMediumRegular opacity={0.66}>
                                        {item.subTitle}
                                    </BodyMediumRegular>
                                </YStack>
                            </XStack>
                        ))}
                    </YStack>
                </View>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={onClose}
                    text="Got it"
                />
            </YStack>
        </Sheet>
    );
};

export default OnboardingSheet;
